import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux'; // Import useSelector
import { selectCurrentUser } from '../../../features/auth/authSlice'; // Import selectCurrentUser
import { useGetPropertyQuery } from '../../../features/api/apiSlice'; // Import useGetPropertyQuery
import Header from '../../../components/ui/Header';
import Footer from '../../../components/ui/Footer';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import HotelBookingForm from './HotelBookingForm';
import { useToast } from '../../../components/ui/Toast'; // Import useToast
import SEO from '../../../components/seo';

const HotelDetail = () => {
  const { slug_id } = useParams();
  const navigate = useNavigate();
  const addToast = useToast(); // Initialize useToast
  const currentUser = useSelector(selectCurrentUser); // Get current user from Redux store
 
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Fetch hotel data using the generic getProperty endpoint
  const { data: apiHotel, isLoading, isError, error } = useGetPropertyQuery(slug_id, {
    skip: !slug_id, // Skip query if slug_id is not available
  });

  const hotel = apiHotel; // Use the fetched data as the hotel object

  // Handle API errors with toasts
  useEffect(() => {
    if (isError) {
      addToast(`Error fetching hotel details: ${error?.message || 'Unknown error'}`, 'error');
    }
  }, [isError, error, addToast]);

  const nextImage = () => {
    if (hotel?.images) {
      setCurrentImageIndex((prev) => (prev + 1) % hotel.images.length);
    }
  };

  const prevImage = () => {
    if (hotel?.images) {
      setCurrentImageIndex((prev) => (prev - 1 + hotel.images.length) % hotel.images.length);
    }
  };

  const selectImage = (index) => {
    setCurrentImageIndex(index);
  };

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, index) => (
      <Icon
        key={index}
        name="Star"
        size={16}
        className={index < Math.floor(rating) ? 'text-yellow-500 fill-current' : 'text-gray-300'}
      />
    ));
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="pt-16 flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading hotel details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (isError || !hotel) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="pt-16 flex items-center justify-center min-h-screen">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-foreground mb-4">Hotel not found</h1>
            <p className="text-muted-foreground">The hotel you are looking for does not exist or an error occurred.</p>
            <Button onClick={() => navigate('/hotels')}>Back to Hotels</Button>
          </div>
        </div>
      </div>
    );
  }

  // Mocked data for fields not directly available in generic property API
  const mockHotelDetails = {
    rating: 4.0, // Mocked
    reviews: 0, // Mocked
    price: hotel.price, // Use actual price
    currency: "UGX", // Assuming UGX
    longDescription: hotel.description, // Use description as longDescription
    amenities: ["WiFi", "Parking"], // Mocked
    rooms: [ // Mocked
      { type: "Standard Room", price: hotel.price, capacity: 2, size: "30 sqm", bed: "King Bed", view: "City View", amenities: ["Free WiFi"], available: 5, images: [hotel.title_image] }
    ],
    policies: { // Mocked
      checkIn: "2:00 PM", checkOut: "12:00 PM", cancellation: "Free cancellation", pets: "No pets", smoking: "Non-smoking", parking: "Free parking"
    },
    contact: { // Mocked
      phone: "+256 123 456 789", email: "info@hotel.com", website: "www.hotel.com"
    },
    location: { // Mocked
      address: hotel.address, coordinates: { lat: parseFloat(hotel.latitude), lng: parseFloat(hotel.longitude) }, nearby: ["City Center"]
    }
  };

  // Merge API data with mock data for missing fields
  const displayHotel = {
    ...mockHotelDetails,
    ...hotel,
    images: hotel.gallery?.map(img => img.image_url) || [hotel.title_image], // Use gallery images if available
    name: hotel.title, // Map title to name
    location: {
      ...mockHotelDetails.location,
      address: hotel.address,
      coordinates: { lat: parseFloat(hotel.latitude), lng: parseFloat(hotel.longitude) }
    }
  };

  const hotelSchema = {
    '@context': 'https://schema.org',
    '@type': 'Hotel',
    name: displayHotel.name,
    description: displayHotel.description || 'Hotel listing on HireBroker with rooms, pricing and booking details.',
    url: `https://hirebroker.co/hotels/${slug_id}`,
    image: displayHotel.title_image,
    address: {
      '@type': 'PostalAddress',
      streetAddress: displayHotel.address || '',
      addressLocality: displayHotel.city || '',
      addressCountry: 'UG'
    },
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: displayHotel.rating || 4,
      reviewCount: displayHotel.reviews || 0
    }
  };

  if (showBookingForm) {
    return (
      <HotelBookingForm
        hotel={displayHotel}
        onClose={() => setShowBookingForm(false)}
        onBack={() => setShowBookingForm(false)}
      />
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title={`${displayHotel.name} | Hotel Listing`}
        description={displayHotel.description || `Explore this hotel listing on HireBroker and contact our team for travel rental support.`}
        canonicalUrl={`https://hirebroker.co/hotels/${slug_id}`}
        ogImage={displayHotel.title_image}
        schema={hotelSchema}
      />
      <Header />

      <main className="pt-16">
        {/* Hero Image Gallery */}
        <div className="relative h-96 md:h-[500px] bg-muted">
          <img
            src={displayHotel.images[currentImageIndex]}
            alt={displayHotel.name}
            className="w-full h-full object-cover"
          />

          {/* Navigation Arrows */}
          <button
            onClick={prevImage}
            className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/50 hover:bg-black/70 text-white rounded-full flex items-center justify-center transition-colors duration-150"
            aria-label="Previous image"
          >
            <Icon name="ChevronLeft" size={20} />
          </button>
          <button
            onClick={nextImage}
            className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/50 hover:bg-black/70 text-white rounded-full flex items-center justify-center transition-colors duration-150"
            aria-label="Next image"
          >
            <Icon name="ChevronRight" size={20} />
          </button>

          {/* Image Counter */}
          <div className="absolute bottom-4 right-4 bg-black/50 text-white px-3 py-1 rounded-full text-sm">
            {currentImageIndex + 1} / {displayHotel.images.length}
          </div>

          {/* Back Button */}
          <button
            onClick={() => navigate('/hotels')}
            className="absolute top-4 left-4 w-10 h-10 bg-black/50 hover:bg-black/70 text-white rounded-full flex items-center justify-center transition-colors duration-150"
            aria-label="Go back"
          >
            <Icon name="ArrowLeft" size={20} />
          </button>
        </div>

        {/* Thumbnail Navigation */}
        <div className="bg-card border-b border-border">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex gap-2 overflow-x-auto pb-2">
              {displayHotel.images.map((image, index) => (
                <button
                  key={index}
                  onClick={() => selectImage(index)}
                  className={`flex-shrink-0 w-20 h-16 rounded-lg overflow-hidden border-2 transition-all duration-150 ${
                    index === currentImageIndex
                      ? 'border-primary shadow-md'
                      : 'border-border hover:border-primary/50'
                  }`}
                >
                  <img
                    src={image}
                    alt={`Thumbnail ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-8">
              {/* Hotel Header */}
              <div>
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-2">
                      {displayHotel.name}
                    </h1>
                    <div className="flex items-center text-muted-foreground mb-3">
                      <Icon name="MapPin" size={18} className="mr-2" />
                      <span>{displayHotel.location.address}</span>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="flex items-center">
                        {renderStars(displayHotel.rating)}
                        <span className="ml-2 text-sm font-medium">{displayHotel.rating}</span>
                      </div>
                      <span className="text-sm text-muted-foreground">
                        ({displayHotel.reviews} reviews)
                      </span>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-primary">
                      From {displayHotel.currency} {displayHotel.price}
                    </div>
                    <div className="text-sm text-muted-foreground">per night</div>
                  </div>
                </div>

                {/* Quick Actions */}
                <div className="flex flex-wrap gap-3">
                  <Button
                    onClick={() => setShowBookingForm(true)}
                    className="min-w-[150px]"
                  >
                    <Icon name="Calendar" size={16} className="mr-2" />
                    Book Now
                  </Button>
                  <Button variant="outline">
                    <Icon name="Heart" size={16} className="mr-2" />
                    Save
                  </Button>
                  <Button variant="outline">
                    <Icon name="Share" size={16} className="mr-2" />
                    Share
                  </Button>
                </div>
              </div>

              {/* Description */}
              <div>
                <h2 className="text-2xl font-semibold text-foreground mb-4">About This Hotel</h2>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  {displayHotel.description}
                </p>
                <p className="text-muted-foreground leading-relaxed">
                  {displayHotel.longDescription}
                </p>
              </div>

              {/* Amenities */}
              <div>
                <h2 className="text-2xl font-semibold text-foreground mb-4">Amenities & Services</h2>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                  {displayHotel.amenities.map((amenity, index) => (
                    <div key={index} className="flex items-center">
                      <Icon name="Check" size={16} className="text-green-500 mr-2" />
                      <span className="text-sm">{amenity}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Room Types */}
              <div>
                <h2 className="text-2xl font-semibold text-foreground mb-4">Room Types</h2>
                <div className="space-y-4">
                  {displayHotel.rooms.map((room, index) => (
                    <div key={index} className="border border-border rounded-lg p-6">
                      <div className="flex flex-col md:flex-row md:items-center justify-between mb-4">
                        <div>
                          <h3 className="text-lg font-semibold text-foreground mb-1">{room.type}</h3>
                          <div className="flex items-center gap-4 text-sm text-muted-foreground mb-2">
                            <span>{room.capacity} guests</span>
                            <span>{room.size}</span>
                            <span>{room.bed}</span>
                          </div>
                          <div className="flex items-center text-sm text-muted-foreground">
                            <Icon name="Eye" size={14} className="mr-1" />
                            {room.view}
                          </div>
                        </div>
                        <div className="text-right mt-4 md:mt-0">
                          <div className="text-xl font-bold text-primary">
                            {displayHotel.currency} {room.price}
                          </div>
                          <div className="text-sm text-muted-foreground">per night</div>
                          <div className="text-sm text-green-600 font-medium">
                            {room.available} available
                          </div>
                        </div>
                      </div>

                      <div className="flex flex-wrap gap-2 mb-4">
                        {room.amenities.map((amenity, idx) => (
                          <span key={idx} className="px-2 py-1 bg-muted text-xs rounded-full">
                            {amenity}
                          </span>
                        ))}
                      </div>

                      <Button
                        onClick={() => setShowBookingForm(true)}
                        disabled={room.available === 0}
                        className="w-full md:w-auto"
                      >
                        {room.available === 0 ? 'Sold Out' : 'Book This Room'}
                      </Button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Location & Nearby */}
              <div>
                <h2 className="text-2xl font-semibold text-foreground mb-4">Location & Nearby Attractions</h2>
                <div className="bg-muted/50 rounded-lg p-4 mb-4">
                  <div className="flex items-start">
                    <Icon name="MapPin" size={20} className="text-primary mr-3 mt-1" />
                    <div>
                      <h3 className="font-medium text-foreground mb-1">Address</h3>
                      <p className="text-muted-foreground">{displayHotel.location.address}</p>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {displayHotel.location.nearby.map((place, index) => (
                    <div key={index} className="flex items-center justify-between p-3 border border-border rounded-lg">
                      <div>
                        <h4 className="font-medium text-foreground">{place.name}</h4>
                        <p className="text-sm text-muted-foreground">{place.type}</p>
                      </div>
                      <span className="text-sm font-medium text-primary">{place.distance}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Policies */}
              <div>
                <h2 className="text-2xl font-semibold text-foreground mb-4">Hotel Policies</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="font-medium text-foreground mb-3">Check-in & Check-out</h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span>Check-in:</span>
                        <span className="font-medium">{displayHotel.policies.checkIn}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Check-out:</span>
                        <span className="font-medium">{displayHotel.policies.checkOut}</span>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="font-medium text-foreground mb-3">Important Policies</h3>
                    <div className="space-y-2 text-sm text-muted-foreground">
                      <p>{displayHotel.policies.cancellation}</p>
                      <p>{displayHotel.policies.pets}</p>
                      <p>{displayHotel.policies.smoking}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="bg-card border border-border rounded-lg p-6 sticky top-24">
                <h3 className="text-lg font-semibold text-foreground mb-4">Quick Booking</h3>

                <div className="space-y-4 mb-6">
                  <div className="text-center p-4 bg-primary/5 rounded-lg">
                    <div className="text-2xl font-bold text-primary mb-1">
                      From {displayHotel.currency} {displayHotel.price}
                    </div>
                    <div className="text-sm text-muted-foreground">per night</div>
                  </div>

                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Rating:</span>
                      <div className="flex items-center">
                        {renderStars(displayHotel.rating)}
                        <span className="ml-1">{displayHotel.rating}</span>
                      </div>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Reviews:</span>
                      <span>{displayHotel.reviews}</span>
                    </div>
                  </div>
                </div>

                <Button
                  onClick={() => setShowBookingForm(true)}
                  className="w-full mb-3"
                >
                  <Icon name="Calendar" size={16} className="mr-2" />
                  Book Your Stay
                </Button>

                <div className="text-center">
                  <p className="text-sm text-muted-foreground mb-2">Need help?</p>
                  <div className="space-y-1 text-xs text-muted-foreground">
                    <p>📞 {displayHotel.contact.phone}</p>
                    <p>✉️ {displayHotel.contact.email}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default HotelDetail;
